import { useState, useEffect } from 'react';
import { SellOnlineSwitch, useStoreFeature } from '../components/commerce/SellOnline';
import { backdropClose } from '../utils/backdropClose';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { Plus, Search, Pencil, ToggleLeft, ToggleRight, Package, X, Loader2 } from 'lucide-react';

interface Product {
  id: string;
  sellOnline?: boolean;
  gymId: string;
  name: string;
  sku: string | null;
  price: number;
  gstPercent: number;
  category: string | null;
  stock: number | null;
  description: string | null;
  isActive: boolean;
  createdAt: string;
}

function ProductModal({
  product,
  onClose,
  onSaved,
  gymId
}: {
  product: Partial<Product> | null;
  onClose: () => void;
  onSaved: () => void;
  gymId: string;
}) {
  const isEdit = !!product?.id;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: product?.name || '',
    sku: product?.sku || '',
    price: product?.price?.toString() || '',
    gstPercent: product?.gstPercent?.toString() || '18',
    category: product?.category || '',
    stock: product?.stock?.toString() || '',
    description: product?.description || '',
  });

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.price) return toast.error('Price is required');
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const body = {
        name: form.name.trim(),
        sku: form.sku.trim() || null,
        price: parseFloat(form.price),
        gstPercent: parseFloat(form.gstPercent) || 0,
        category: form.category.trim() || null,
        stock: form.stock ? parseInt(form.stock) : null,
        description: form.description.trim() || null,
        ...(!isEdit && { gymId })
      };

      const url = isEdit ? `${API_BASE_URL}/api/products/${product!.id}` : `${API_BASE_URL}/api/products`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed');
      toast.success(isEdit ? 'Product updated' : 'Product created');
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1001] p-0 sm:p-4" {...backdropClose(onClose)}>
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[90dvh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-5 text-white shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-black">{isEdit ? 'Edit Product' : 'New Product'}</h2>
              <p className="text-white/70 text-xs">Catalog Item</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 bg-white/10 hover:bg-red-500/80 rounded-xl border border-white/20 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label-xs">Product Name *</label>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Protein Powder" className="field-input" required />
            </div>
            <div>
              <label className="label-xs">SKU</label>
              <input type="text" value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="Optional" className="field-input" />
            </div>
            <div>
              <label className="label-xs">Category</label>
              <input type="text" value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Supplements" className="field-input" />
            </div>
            <div>
              <label className="label-xs">Price (₹) *</label>
              <input type="number" min={0} value={form.price} onChange={e => set('price', e.target.value)} placeholder="0.00" className="field-input" required />
            </div>
            <div>
              <label className="label-xs">GST %</label>
              <input type="number" min={0} max={100} value={form.gstPercent} onChange={e => set('gstPercent', e.target.value)} className="field-input" />
            </div>
            {/* Stock Quantity field hidden until inventory management is introduced */}
            <div className="col-span-2">
              <label className="label-xs">Description</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className="field-input resize-none" placeholder="Brief description..." />
            </div>
          </div>
        </form>

        <div className="p-5 border-t border-gray-100 shrink-0">
          <button onClick={handleSubmit as any} disabled={saving} className="w-full py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-black hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-100 disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Product'}
          </button>
        </div>
      </div>
      <style>{`
        .label-xs { display:block; font-size:10px; font-weight:900; text-transform:uppercase; letter-spacing:.1em; color:#9ca3af; margin-bottom:6px; }
        .field-input { width:100%; padding:10px 14px; border-radius:12px; border:1px solid #e5e7eb; font-size:14px; outline:none; transition:all .15s; background:#f9fafb; }
        .field-input:focus { border-color:#f59e0b; box-shadow:0 0 0 3px rgb(245 158 11/.1); background:white; }
      `}</style>
    </div>
  );
}

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const { storeFeature } = useStoreFeature();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modalProduct, setModalProduct] = useState<Partial<Product> | null | false>(false);
  const [gymId, setGymId] = useState('');

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const params = new URLSearchParams();
      if (showInactive) params.set('includeInactive', 'true');
      if (search) params.set('search', search);
      const res = await fetch(`${API_BASE_URL}/api/products?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      const d = await res.json();
      setProducts(d.data || []);
      if ((d.data || []).length > 0 && !gymId) setGymId(d.data[0].gymId);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      fetch(`${API_BASE_URL}/api/gyms`, { headers: { Authorization: `Bearer ${session.access_token}` } })
        .then(r => r.json()).then(d => { if (d.data?.[0]?.id) setGymId(d.data[0].id); }).catch(() => {});
    });
  }, []);

  useEffect(() => { fetchProducts(); }, [search, showInactive]);

  const toggleActive = async (product: Product) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !product.isActive })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      toast.success(product.isActive ? 'Product deactivated' : 'Product activated');
      fetchProducts();
    } catch (err: any) { toast.error(err.message || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-50/50 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-amber-100/50"></div>
        <div className="relative">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">Product Catalog</h1>
          <p className="text-[10px] font-black text-gray-400 mt-2 uppercase tracking-[0.2em]">Inventory & Sales Items</p>
        </div>

        <button
          onClick={() => setModalProduct({})}
          className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-amber-100 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Product</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 outline-none bg-white"
          />
        </div>
        <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer text-sm font-bold text-gray-600 select-none">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="accent-amber-500" />
          Show inactive
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-3xl bg-amber-50 border border-amber-100 flex items-center justify-center mb-5">
            <Package className="w-10 h-10 text-amber-200" />
          </div>
          <p className="font-black text-gray-400 text-lg">No products yet</p>
          <p className="text-sm text-gray-300 mt-1">Add products like supplements, gear, or accessories</p>
          <button onClick={() => setModalProduct({})} className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-dashed border-amber-200 text-amber-500 text-sm font-black hover:bg-amber-50 transition-colors">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="text-left px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Name</th>
                  <th className="text-left px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hidden sm:table-cell">SKU</th>
                  <th className="text-left px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hidden md:table-cell">Category</th>
                  <th className="text-right px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Price</th>
                  <th className="text-center px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hidden sm:table-cell">GST</th>
                  {/* Stock column hidden until inventory management is introduced */}
                  <th className="text-center px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                  {storeFeature && <th className="text-center px-4 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Store</th>}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {products.map(p => (
                  <tr key={p.id} className={`hover:bg-gray-50/50 transition-colors ${!p.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-4">
                      <p className="font-bold text-gray-900">{p.name}</p>
                      {p.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{p.description}</p>}
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell text-xs text-gray-500 font-mono">{p.sku || '—'}</td>
                    <td className="px-4 py-4 hidden md:table-cell text-xs text-gray-500">{p.category || '—'}</td>
                    <td className="px-4 py-4 text-right font-black text-gray-800">₹{Number(p.price).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-4 text-center text-xs text-gray-500 hidden sm:table-cell">{p.gstPercent}%</td>
                    {/* Stock cell hidden until inventory management is introduced */}
                    <td className="px-4 py-4 text-center">
                      <button onClick={() => toggleActive(p)} title={p.isActive ? 'Deactivate' : 'Activate'}>
                        {p.isActive
                          ? <ToggleRight className="w-6 h-6 text-green-500 mx-auto" />
                          : <ToggleLeft className="w-6 h-6 text-gray-300 mx-auto" />}
                      </button>
                    </td>
                    {storeFeature && (
                      <td className="px-4 py-4 text-center">
                        <SellOnlineSwitch endpoint={`/api/products/${p.id}`} sellOnline={!!p.sellOnline}
                          onChanged={v => setProducts(list => list.map(x => x.id === p.id ? { ...x, sellOnline: v } : x))} />
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <button
                        onClick={() => setModalProduct(p)}
                        className="p-2 rounded-lg hover:bg-amber-50 hover:text-amber-600 text-gray-400 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalProduct !== false && (
        <ProductModal
          product={modalProduct}
          gymId={gymId}
          onClose={() => setModalProduct(false)}
          onSaved={fetchProducts}
        />
      )}
    </div>
  );
}
